/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  SavedSearchCreateType,
  SavedSearchDirectoryGetType,
  SavedSearchDirectoryType,
  SavedSearchMoveType,
  SavedSearchRequestType,
  SavedSearchViewingModeEnum,
  SavedSearchViewingOrderEnum,
} from 'types/savedSearchType';

import safeAxios from 'shared/api/safeFetch';

import {
  clearSavedSearchHistoryFailed,
  clearSavedSearchHistorySuccess,
  createSavedSearchDirectoryFailed,
  createSavedSearchDirectorySuccess,
  createSavedSearchFailed,
  createSavedSearchSuccess,
  deleteSavedSearchDirectoryFailed,
  deleteSavedSearchDirectorySuccess,
  deleteSavedSearchFailed,
  deleteSavedSearchSuccess,
  getSavedSearchDirectoriesFailed,
  getSavedSearchDirectoriesSuccess,
  getSavedSearchDirectorySuccess,
  getSavedSearchFailed,
  getSavedSearchHistorySuccess,
  getSavedSearchIdFailed,
  getSavedSearchIdSuccess,
  getSavedSearchRecentSuccess,
  getSavedSearchResultsFailed,
  getSavedSearchResultsSuccess,
  getSavedSearchSuccess,
  moveSavedSearchFailed,
  moveSavedSearchSuccess,
  patchSavedSearchFailed,
  patchSavedSearchSuccess,
  setRedirectSavedSearch,
} from 'redux/actions/savedSearchActions';
import {
  CLEAR_SAVED_SEARCH_HISTORY,
  CREATE_SAVED_SEARCH,
  CREATE_SAVED_SEARCH_DIRECTORY,
  DELETE_SAVED_SEARCH,
  DELETE_SAVED_SEARCH_DIRECTORY,
  GET_SAVED_SEARCH,
  GET_SAVED_SEARCH_DIRECTORIES,
  GET_SAVED_SEARCH_DIRECTORY,
  GET_SAVED_SEARCH_HISTORY,
  GET_SAVED_SEARCH_ID,
  GET_SAVED_SEARCH_RECENT,
  GET_SAVED_SEARCH_RESULT,
  MOVE_SAVED_SEARCH,
  PATCH_SAVED_SEARCH,
} from 'redux/constants/savedSearchConstants';
import config from 'shared/config';
import { ErrorResponseType } from 'types/reduxTypes';
import { RootState } from 'types/stateTypes';
import { getUserUref } from 'utils/user.utils';
import { ActionType } from '../actions/themeActions';

const { apiUrl } = config;

export const SAVED_SEARCH_PAGE_SIZE = 10;

function* getSavedSearchRequestHandler(action: ActionType<SavedSearchRequestType>) {
  const {
    page = 1,
    pageSize = SAVED_SEARCH_PAGE_SIZE,
    mode = SavedSearchViewingModeEnum.bookmarked,
    order = SavedSearchViewingOrderEnum.updateDate,
    directoryId,
  } = action.payload;
  const type = action.type;
  try {
    let requestParams = `page=${page}&pageSize=${pageSize}&order=${order}&mode=${mode}`;
    if (directoryId) requestParams += `&directoryId=${directoryId}`;
    const { data } = yield call(safeAxios, `${apiUrl}/saved-search?${requestParams}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });

    if (type === GET_SAVED_SEARCH_RECENT) {
      yield put(getSavedSearchRecentSuccess({ data }));
    } else if (type === GET_SAVED_SEARCH_HISTORY) {
      yield put(getSavedSearchHistorySuccess({ data }));
    } else {
      yield put(getSavedSearchSuccess({ data, page }));
    }
  } catch (error) {
    console.error('getSavedSearchError', error);
    const err = error as unknown as ErrorResponseType;
    yield put(getSavedSearchFailed(err.message as unknown as string));
  }
}

function* getSavedSearchIdRequestHandler(action: ActionType<{ id: string }>) {
  const id = action.payload;
  try {
    const { data } = yield call(safeAxios, `${apiUrl}/saved-search/${id}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    yield put(getSavedSearchIdSuccess(data));
  } catch (error) {
    console.error('getSavedSearchError', error);
    const err = error as unknown as ErrorResponseType;
    yield put(getSavedSearchIdFailed(err.message as unknown as string));
  }
}

function* getSavedSearchResultHandler(action: ActionType<{ id: string }>) {
  const id = action.payload;
  try {
    const { data } = yield call(safeAxios, `${apiUrl}/saved-search/${id}/results`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });

    yield put(getSavedSearchResultsSuccess(data));
  } catch (error) {
    console.error('getSavedSearchResultsError', error);
    const err = error as unknown as ErrorResponseType;
    yield put(getSavedSearchResultsFailed(err.message as unknown as string));
  }
}

function* createSavedSearchHandler(action: ActionType<SavedSearchCreateType>) {
  const postData = action.payload;
  try {
    const { data: requestId } = yield call(safeAxios, `${apiUrl}/saved-search`, {
      body: postData,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    yield put(createSavedSearchSuccess(requestId));

    const { directoryId } = postData;
    if (directoryId) {
      yield call(safeAxios, `${apiUrl}/saved-search/move/${requestId}?toDir=${directoryId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
      });

      yield put(moveSavedSearchSuccess(directoryId));
    }

    yield put(setRedirectSavedSearch());
  } catch (error) {
    console.error('createSavedSearchError', error);
    const err = error as unknown as ErrorResponseType;
    yield put(createSavedSearchFailed(err.message as unknown as string));
  }
}

function* patchSavedSearchHandler(action: ActionType<SavedSearchCreateType>) {
  const postData = action.payload;
  const { id, directoryId } = action.payload;
  try {
    yield call(safeAxios, `${apiUrl}/saved-search/${id}`, {
      body: postData,
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'PATCH',
    });

    yield put(patchSavedSearchSuccess());

    if (directoryId) {
      yield call(safeAxios, `${apiUrl}/saved-search/move/${id}?toDir=${directoryId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
      });

      yield put(moveSavedSearchSuccess(directoryId));
    }

    yield put(setRedirectSavedSearch());
  } catch (error) {
    console.error('patchSavedSearchError', error);
    const err = error as unknown as ErrorResponseType;
    yield put(patchSavedSearchFailed(err.message as unknown as string));
  }
}

function* deleteSavedSearchHandler(action: ActionType<string>) {
  const id = action.payload;

  try {
    yield call(safeAxios, `${apiUrl}/saved-search/${id}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    });

    yield put(deleteSavedSearchSuccess(id));
  } catch (error) {
    const err = error as unknown as ErrorResponseType;
    console.error('deleteSavedSearchError', err.message);
    yield put(deleteSavedSearchFailed(err.message as unknown as string));
  }
}

function* moveSavedSearchHandler(action: ActionType<SavedSearchMoveType>) {
  const { id, toDir } = action.payload;
  try {
    yield call(safeAxios, `${apiUrl}/saved-search/move/${id}?toDir=${toDir || ''}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'PATCH',
    });

    yield put(moveSavedSearchSuccess(id));
  } catch (error) {
    console.error('createSavedSearchError', error);
    const err = error as unknown as ErrorResponseType;
    yield put(moveSavedSearchFailed(err.message as unknown as string));
  }
}

function* createDirectorySavedSearchHandler(action: ActionType<string>) {
  const { auth }: RootState = yield select();
  const rUser = getUserUref(auth.id);
  if (!rUser) {
    return;
  }
  const body = { name: action.payload, rUser };
  try {
    const { data } = yield call(safeAxios, `${apiUrl}/saved-search/directory`, {
      body,
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    yield put(
      createSavedSearchDirectorySuccess({
        getrUser: rUser,
        id: data,
        name: action.payload,
        objectCount: null,
        pinned: false,
      })
    );
  } catch (error) {
    const err = error as unknown as ErrorResponseType;
    console.error('createSavedSearchDirectoryError', err.message);
    yield put(createSavedSearchDirectoryFailed({ message: err.message || '' }));
  }
}

function* getDirectoriesSavedSearchHandler(action: ActionType<SavedSearchDirectoryGetType>) {
  const { page = 1, pageSize = 10 } = action.payload;
  const { auth }: RootState = yield select();
  const rUser = getUserUref(auth.id);
  if (!rUser) {
    return;
  }
  try {
    const { data }: { data: SavedSearchDirectoryType[] } = yield call(
      safeAxios,
      `${apiUrl}/saved-search/directory/?page=${page}&pageSize=${pageSize}&rUser=${rUser}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }
    );

    yield put(
      getSavedSearchDirectoriesSuccess({
        directories: data,
        hasMore: data.length === pageSize,
      })
    );
  } catch (error) {
    const err = error as unknown as ErrorResponseType;
    console.error('createSavedSearchDirectoryError', err.message);
    yield put(getSavedSearchDirectoriesFailed({ message: err.message || '' }));
  }
}

function* getDirectorySavedSearchHandler(action: ActionType<string>) {
  const directoryId = action.payload;
  const { auth }: RootState = yield select();
  const rUser = getUserUref(auth.id);
  if (!rUser) {
    return;
  }
  try {
    const { data }: { data: SavedSearchDirectoryType } = yield call(
      safeAxios,
      `${apiUrl}/saved-search/directory/${directoryId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }
    );

    yield put(getSavedSearchDirectorySuccess(data));
  } catch (error) {
    const err = error as unknown as ErrorResponseType;
    console.error('createSavedSearchDirectoryError', err.message);
    yield put(getSavedSearchDirectoriesFailed({ message: err.message || '' }));
  }
}

function* deleteDirectorySavedSearchHandler(action: ActionType<string>) {
  const id = action.payload;

  try {
    yield call(safeAxios, `${apiUrl}/saved-search/directory/${id}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    });

    yield put(deleteSavedSearchDirectorySuccess(id));
  } catch (error) {
    const err = error as unknown as ErrorResponseType;
    console.error('deleteSavedSearchDirectoryError', err.message);
    yield put(deleteSavedSearchDirectoryFailed(err.message as unknown as string));
  }
}

function* clearSavedSearchHistoryHandler() {
  try {
    yield call(safeAxios, `${apiUrl}/saved-search/clear`, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    });

    yield put(clearSavedSearchHistorySuccess());
  } catch (error) {
    const err = error as unknown as ErrorResponseType;
    console.error('clearSavedSearchHistoryError', err.message);
    yield put(clearSavedSearchHistoryFailed(err.message as unknown as string));
  }
}

export default function* savedSearchWatcher(): Generator {
  yield takeEvery(GET_SAVED_SEARCH, getSavedSearchRequestHandler);
  yield takeEvery(GET_SAVED_SEARCH_RECENT, getSavedSearchRequestHandler);
  yield takeEvery(GET_SAVED_SEARCH_HISTORY, getSavedSearchRequestHandler);
  yield takeEvery(CREATE_SAVED_SEARCH_DIRECTORY, createDirectorySavedSearchHandler);
  yield takeEvery(PATCH_SAVED_SEARCH, patchSavedSearchHandler);
  yield takeEvery(GET_SAVED_SEARCH_ID, getSavedSearchIdRequestHandler);
  yield takeEvery(CREATE_SAVED_SEARCH, createSavedSearchHandler);
  yield takeEvery(DELETE_SAVED_SEARCH, deleteSavedSearchHandler);
  yield takeEvery(MOVE_SAVED_SEARCH, moveSavedSearchHandler);
  yield takeEvery(GET_SAVED_SEARCH_DIRECTORIES, getDirectoriesSavedSearchHandler);
  yield takeEvery(GET_SAVED_SEARCH_DIRECTORY, getDirectorySavedSearchHandler);
  yield takeEvery(DELETE_SAVED_SEARCH_DIRECTORY, deleteDirectorySavedSearchHandler);
  yield takeEvery(GET_SAVED_SEARCH_RESULT, getSavedSearchResultHandler);
  yield takeEvery(CLEAR_SAVED_SEARCH_HISTORY, clearSavedSearchHistoryHandler);
}
